import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { invalidateCachedAsset } from "../../app/slices/AssetsSlice";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { AssetDetailsDTO, AssetVariantDTO } from "../../dto/AssetDTO";
import { AssetService } from "../../services/AssetService";

export type LanguageUpload =
  | { status: "uploading"; progress: number; localUrl: string }
  | { status: "added"; localUrl: string }
  | { status: "failed"; reason: "upload" | "type" };

/**
 * Per-language uploads for one asset: add a language's own variant, or replace it. Progress and the
 * outcome live here, per language code, so each row can show them in place. Success invalidates the
 * asset in every preview cache, so the list and the previews refetch the new variant. The local file
 * stays the row's thumbnail meanwhile — the server encodes a new variant before its thumbnail exists.
 */
export const useLanguageUploads = (asset: AssetDetailsDTO | undefined) => {
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const dispatch = useDispatch();
  const [uploads, setUploads] = useState<Record<string, LanguageUpload>>({});
  const urls = useRef<string[]>([]);

  useEffect(() => () => urls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const set = (code: string, upload: LanguageUpload | undefined) =>
    setUploads((prev) => {
      const next = { ...prev };
      if (upload) next[code] = upload;
      else delete next[code];
      return next;
    });

  const upload = useCallback(
    async (languageCode: string, file: File, replacing?: AssetVariantDTO) => {
      if (!asset || !orgId) return;
      const localUrl = URL.createObjectURL(file);
      urls.current.push(localUrl);
      set(languageCode, { status: "uploading", progress: 0, localUrl });
      const onProgress = (progress: number) => set(languageCode, { status: "uploading", progress, localUrl });

      try {
        if (replacing) {
          await AssetService.ReplaceVariantContent(orgId, token, asset.id, replacing.id, file, onProgress);
        } else {
          await AssetService.CreateLanguageVariant(orgId, token, asset.id, languageCode, file, onProgress);
        }
        set(languageCode, { status: "added", localUrl });
        dispatch(invalidateCachedAsset(asset.assetId));
      } catch (error) {
        console.error("Uploading a language variant failed", error);
        set(languageCode, { status: "failed", reason: "upload" });
      }
    },
    [asset, orgId, token, dispatch]
  );

  const fail = useCallback((languageCode: string, reason: "type") => set(languageCode, { status: "failed", reason }), []);

  return { uploads, upload, fail } as const;
};
